import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Mobile-only "← Back" + title strip for top-level inner pages
 * (Account, Invoices, Orders). Desktop has the PortalHeader's nav
 * to backtrack with, but mobile users only get the BottomNav, which
 * doesn't get them out of a deep flow they reached from the
 * dashboard. Hidden at md+ so the desktop layout doesn't end up
 * with a redundant duplicate of the page title.
 *
 * Two back-navigation modes:
 *
 *  - `backPath` (depth-based, default for nested pages): always
 *    navigates to a specific parent path on tap. Use this when the
 *    page sits at a known position in the app's depth hierarchy
 *    and the customer's expected "back" target is the parent
 *    regardless of which route they actually came from. Example:
 *    Orders list → Account, Invoices list → Account, Account →
 *    Dashboard. This is consistent: the user's mental model maps
 *    to depth, not to browser history. Without the override, an
 *    e-mailed deep-link to /orders would pop back to wherever the
 *    OS handed off to the WebView (often nowhere useful).
 *
 *  - `fallbackPath` (history-based, legacy): pop one history entry
 *    if available, otherwise navigate to fallbackPath. Used by
 *    pages that ARE at the right history depth and just want the
 *    "browser back" affordance with a sane fallback for fresh-tab
 *    deep-links.
 */
export function MobilePageHeader({
	title,
	backPath,
	fallbackPath = '/dashboard',
	action,
}: {
	title: string;
	/**
	 * Always navigate to this path on back. Takes precedence over
	 * `fallbackPath` and over the history pop. Use for top-level
	 * sections whose depth-parent is fixed (Orders/Invoices →
	 * Account; Account → Dashboard).
	 */
	backPath?: string;
	/**
	 * Used when `backPath` is unset and there's no history entry to
	 * pop — fresh-tab deep-link case. Defaults to /dashboard.
	 */
	fallbackPath?: string;
	/**
	 * Optional right-aligned control rendered in the title row (e.g.
	 * the Invoices page's "Make a Payment" button) so primary page
	 * actions share the header line instead of costing their own row.
	 */
	action?: ReactNode;
}): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const handleBack = (): void => {
		if (backPath != null) {
			void navigate(backPath);
			return;
		}
		// `history.length > 1` is the safest "do we have a real ancestor"
		// signal we get from the browser. Some browsers report 1 even on
		// a fresh tab, others 2 — fall back to a known-good landing path
		// so the button never dead-ends the user on the same page.
		if (window.history.length > 1) {
			void navigate(-1);
			return;
		}
		void navigate(fallbackPath);
	};

	return (
		<div className="md:hidden flex items-center gap-2 mb-3">
			<button
				type="button"
				onClick={handleBack}
				aria-label={t('Back')}
				className="-ml-2 inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-brand-input text-gray-700 dark:text-gray-200"
			>
				<FontAwesomeIcon icon={faArrowLeft} />
			</button>
			<h1 className="text-base font-bold text-gray-900 dark:text-gray-100 truncate">
				{t(title)}
			</h1>
			{action != null ? <div className="ml-auto shrink-0">{action}</div> : null}
		</div>
	);
}
